/**
 * @description First-run company onboarding wizard
 *
 * Key features:
 * - Business info / details / payment setup steps (LynxPay)
 * - packSurface: Welcome → Begin → info + details, Site functions, no payment
 * - Pack titles: Welcome to {platformName} (first-run) / Add company (dropdown)
 * - Business Email helper matches Company Settings (outbound reply-to)
 *
 * @dependencies AuthContext, CompanyContext, PermissionContext, BrandingContext
 *
 * @notes Pack restyle uses brand tokens. Do not skip Welcome on pack.
 *   Pack add-company stays mounted — reset Welcome + form when isOpen
 *   goes false → true, and after a successful pack create (LYN-360 B1).
 */

import React, { useEffect, useRef, useState } from 'react';
import { X, ArrowRight, ArrowLeft, Info, Building2, CreditCard, BarChart3, CheckCircle } from 'lucide-react';
import { INDUSTRY_TYPES } from '../../constants/industryTypes';
import { packIndustryTypes } from '../../constants/packIndustryTypes';
import { BUSINESS_TYPES } from '../../constants/businessTypes';
import { LOCATION_FUNCTIONS } from '../../constants/locationFunctions';
import { US_STATES } from '../../utils/usStates';
import { US_IANA_TIMEZONES, timezoneForUsState } from '../../constants/usIanaTimezones';
import {
  formatCityName,
  formatZipCode,
  formatStreetAddress
} from '../../utils/addressFormatters';
import apiClient from '../../services/api/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { useCompany } from '../../contexts/CompanyContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useBranding } from '../../contexts/BrandingContext';

const MODAL_STEPS = {
  WELCOME: 0,
  BUSINESS_INFO: 1,
  BUSINESS_DETAILS: 2,
  PAYMENT_SETUP: 3
};

const EMPTY_FORM_DATA = {
  businessName: '',
  streetAddress: '',
  addressLine2: '',
  city: '',
  state: '',
  zipCode: '',
  phone: '',
  email: '',
  website: '',
  businessType: '',
  accountingMethod: 'cash',
  timezone: '',
  industryType: '',
  locationFunctions: []
};

const OnboardingModal = ({
  isOpen,
  onClose,
  packSurface = false,
  packTitle,
  allowDismiss = false,
  firstRun = false
}) => {
  const { refreshContext } = useAuth();
  const { refreshCompanyContext } = useCompany();
  const { refreshPermissions } = usePermissions();
  const { branding } = useBranding();
  const platformName = branding.identity.platformName;
  const [currentStep, setCurrentStep] = useState(MODAL_STEPS.WELCOME);
  const packFirstRun = packSurface && (firstRun || (!allowDismiss && !packTitle));
  const packHeading = packTitle || (packFirstRun
    ? `Welcome to ${platformName}`
    : (packSurface ? 'Add company' : null));
  const industryOptions = packSurface ? packIndustryTypes() : INDUSTRY_TYPES;
  const packFieldFocus = packSurface
    ? 'focus:ring-1 focus:ring-brand focus:border-brand'
    : 'focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500';
  const packPrimaryBtn = packSurface
    ? 'w-full py-3 px-6 bg-brand text-gray-900 font-medium rounded-lg hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand transition-colors flex items-center justify-center'
    : 'w-full py-3 px-6 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex items-center justify-center';
  const showPackBack = packSurface && (
    currentStep === MODAL_STEPS.BUSINESS_DETAILS
    || (allowDismiss && currentStep === MODAL_STEPS.BUSINESS_INFO)
  );
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(() => ({ ...EMPTY_FORM_DATA }));
  const [errors, setErrors] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);
  const wasOpenRef = useRef(isOpen);

  const resetPackWizard = () => {
    setCurrentStep(MODAL_STEPS.WELCOME);
    setFormData({ ...EMPTY_FORM_DATA });
    setErrors({});
    setCompanyData(null);
    setShowTooltip(false);
  };

  useEffect(() => {
    if (packSurface && isOpen && !wasOpenRef.current) {
      setCurrentStep(MODAL_STEPS.WELCOME);
      setFormData({ ...EMPTY_FORM_DATA });
      setErrors({});
      setCompanyData(null);
      setShowTooltip(false);
    }
    wasOpenRef.current = isOpen;
  }, [packSurface, isOpen]);

  const detailStepCount = packSurface ? 2 : 3;
  // Only allow closing in payment setup step (LynxPay). Pack surface closes after create.
  const canClose = allowDismiss || (!packSurface && currentStep === MODAL_STEPS.PAYMENT_SETUP);

  // Form validation functions
  const validateBusinessInfo = () => {
    const newErrors = {};

    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    if (!formData.streetAddress.trim()) {
      newErrors.streetAddress = 'Street address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state) {
      newErrors.state = 'State is required';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/.test(formData.zipCode)) {
      newErrors.zipCode = 'Invalid ZIP code format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Business email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    if (formData.website && !/^https?:\/\//.test(formData.website)) {
      newErrors.website = 'Website must start with http:// or https://';
    }
    if (!formData.timezone) {
      newErrors.timezone = 'Timezone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateBusinessDetails = () => {
    const newErrors = {};

    if (!formData.businessType) {
      newErrors.businessType = 'Business type is required';
    }
    if (!formData.accountingMethod) {
      newErrors.accountingMethod = 'Accounting method is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Formatting functions
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    switch (name) {
      case 'phone':
        formattedValue = formatPhoneNumber(value);
        break;
      case 'city':
      case 'streetAddress':
        // Preserve user-entered spacing while typing; format on blur instead.
        formattedValue = value;
        break;
      case 'zipCode':
        formattedValue = formatZipCode(value);
        break;
      default:
        formattedValue = value;
    }

    setFormData((prev) => {
      const next = { ...prev, [name]: formattedValue };
      if (name === 'state' && !prev.timezone) {
        const suggested = timezoneForUsState(formattedValue);
        if (suggested) next.timezone = suggested;
      }
      return next;
    });

    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let finalValue = value;

    switch (name) {
      case 'streetAddress':
        finalValue = formatStreetAddress(value);
        break;
      case 'city':
        finalValue = formatCityName(value);
        break;
      default:
        return;
    }

    if (finalValue !== value) {
      setFormData((prevData) => ({
        ...prevData,
        [name]: finalValue
      }));
    }
  };

  // Step navigation
  const handleGetStarted = () => {
    setCurrentStep(MODAL_STEPS.BUSINESS_INFO);
  };

  const handleBusinessInfoNext = () => {
    if (validateBusinessInfo()) {
      setCurrentStep(MODAL_STEPS.BUSINESS_DETAILS);
    }
  };

  const handleBusinessDetailsNext = async () => {
    if (!validateBusinessDetails()) {
      return;
    }

    setIsLoading(true);

    const submissionData = {
      ...formData,
      streetAddress: formatStreetAddress(formData.streetAddress),
      city: formatCityName(formData.city),
      ...(packSurface ? { locationFunctions: formData.locationFunctions } : {})
    };

    try {
      const response = await apiClient.post('/companies/onboarding', submissionData);

      if (response.data.success) {
        setCompanyData(response.data.company);

        // Refresh all contexts to pick up new company/employee/permission records
        // This allows the dashboard to work with full permissions while still showing the payment setup step
        await refreshContext();           // Refresh AuthContext (user, company, employee)
        await refreshCompanyContext();    // Refresh CompanyContext (triggers PermissionContext via employee change)
        await refreshPermissions();       // Explicitly refresh permissions to ensure Owner/Admin access

        if (packSurface) {
          resetPackWizard();
          onClose(response.data.company?.id || null);
          return;
        }

        setCurrentStep(MODAL_STEPS.PAYMENT_SETUP);
      }
    } catch (error) {
      console.error('Company creation failed:', error);
      setErrors({
        submit: error.response?.data?.error || 'Failed to create company. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > MODAL_STEPS.WELCOME) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSetupPayments = () => {
    // TODO: Navigate to payment setup when implemented
    console.log('Setup payments clicked');
    onClose();
  };

  const handleSetupLater = () => {
    onClose();
  };

  const handleClose = () => {
    if (canClose) {
      onClose();
    }
  };

  // Prevent ESC key closing during company setup
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !canClose) {
      e.preventDefault();
    }
  };

  if (!isOpen) return null;

  // Step content renderers
  const renderWelcomeStep = () => (
    <div className="text-center">
      <div className="flex justify-center mb-6">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
          packSurface ? 'bg-brand/10' : 'bg-indigo-100'
        }`}>
          <Building2 className={`h-8 w-8 ${packSurface ? 'text-brand' : 'text-indigo-600'}`} />
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        {packSurface ? packHeading : `Welcome to ${platformName}`}
      </h2>
      <p className="text-gray-600 mb-8">
        {packFirstRun
          ? 'Set up your first company.'
          : packSurface
            ? 'Setup another company within your organization.'
            : 'Let\'s set up your business account so you can start accepting payments and managing your finances.'}
      </p>

      <div className="space-y-4 mb-8">
        {packSurface ? (
          <>
            <div className="flex items-center text-left">
              <div className="w-8 h-8 bg-brand/10 rounded-full flex items-center justify-center mr-3">
                <Building2 className="h-4 w-4 text-brand" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Company identity</p>
                <p className="text-xs text-gray-500">Legal name, address, and business type</p>
              </div>
            </div>
            <div className="flex items-center text-left">
              <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                <Building2 className="h-4 w-4 text-gray-700" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Site functions</p>
                <p className="text-xs text-gray-500">What this location does — yard, dispatch, call center</p>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center text-left">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <CreditCard className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Accept Payments</p>
                <p className="text-xs text-gray-500">Process credit cards, ACH, and digital payments</p>
              </div>
            </div>

            <div className="flex items-center text-left">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <BarChart3 className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Financial Management</p>
                <p className="text-xs text-gray-500">Track invoices, expenses, and financial reports</p>
              </div>
            </div>

            <div className="flex items-center text-left">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <Building2 className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Business Tools</p>
                <p className="text-xs text-gray-500">Customer management and business analytics</p>
              </div>
            </div>
          </>
        )}
      </div>

      <button
        onClick={handleGetStarted}
        className={packPrimaryBtn}
      >
        {packSurface ? 'Begin' : 'Get Started'}
        <ArrowRight className="ml-2 h-4 w-4" />
      </button>

      <p className="mt-4 text-xs text-gray-500">
        Setup takes about 2-3 minutes
      </p>
    </div>
  );

  const renderPackStepHeader = (subtitle) => (
    <div className="flex items-center mb-4 min-h-[3.25rem]">
      {showPackBack ? (
        <button
          type="button"
          onClick={handleBack}
          className="mr-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          disabled={isLoading}
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      ) : (
        <span className="mr-3 w-7" aria-hidden="true" />
      )}
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{packHeading}</h2>
        <p className="text-sm text-gray-600">{subtitle}</p>
      </div>
    </div>
  );

  const renderBusinessInfoStep = () => (
    <div className={`space-y-4 ${packSurface ? 'flex flex-col min-h-[28rem]' : ''}`}>
      {packSurface ? renderPackStepHeader('Tell us about your business location and contact details') : (
      <div className="flex items-center mb-4">
        <button
          onClick={handleBack}
          className="mr-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Business Information</h2>
          <p className="text-sm text-gray-600">Tell us about your business location and contact details</p>
        </div>
      </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Business Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="businessName"
          value={formData.businessName}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
            errors.businessName ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Your legal business name"
        />
        {errors.businessName && (
          <p className="mt-1 text-xs text-red-500">{errors.businessName}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Street Address <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          name="streetAddress"
          value={formData.streetAddress}
          onChange={handleChange}
          onBlur={handleBlur}
          className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
            errors.streetAddress ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="123 Main Street"
        />
        {errors.streetAddress && (
          <p className="mt-1 text-xs text-red-500">{errors.streetAddress}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Address Line 2 <span className="text-gray-400 text-xs">(Optional)</span>
        </label>
        <input
          type="text"
          name="addressLine2"
          value={formData.addressLine2}
          onChange={handleChange}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${packFieldFocus} transition-colors`}
          placeholder="Suite, Unit, Building, Floor, etc."
        />
      </div>

      <div className="grid grid-cols-6 gap-3">
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="city"
            value={formData.city}
            onChange={handleChange}
            onBlur={handleBlur}
            className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
              errors.city ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.city && (
            <p className="mt-1 text-xs text-red-500">{errors.city}</p>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State <span className="text-red-500">*</span>
          </label>
          <select
            name="state"
            value={formData.state}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
              errors.state ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select</option>
            {US_STATES.map(state => (
              <option key={state.code} value={state.code}>
                {state.name}
              </option>
            ))}
          </select>
          {errors.state && (
            <p className="mt-1 text-xs text-red-500">{errors.state}</p>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            ZIP Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
            maxLength="10"
            className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
              errors.zipCode ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="12345"
          />
          {errors.zipCode && (
            <p className="mt-1 text-xs text-red-500">{errors.zipCode}</p>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Timezone <span className="text-red-500">*</span>
        </label>
        <select
          name="timezone"
          value={formData.timezone}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
            errors.timezone ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select timezone</option>
          {US_IANA_TIMEZONES.map((zone) => (
            <option key={zone.value} value={zone.value}>
              {zone.label}
            </option>
          ))}
        </select>
        {errors.timezone && (
          <p className="mt-1 text-xs text-red-500">{errors.timezone}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Business Phone <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
            errors.phone ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="(555) 123-4567"
        />
        {errors.phone && (
          <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Business Email <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-1">
          Your reply-to email used in outbound emails.
        </p>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="billing@example.com"
        />
        {errors.email && (
          <p className="mt-1 text-xs text-red-500">{errors.email}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Website <span className="text-gray-400 text-xs">(Optional)</span>
        </label>
        <input
          type="url"
          name="website"
          value={formData.website}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
            errors.website ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="https://example.com"
        />
        {errors.website && (
          <p className="mt-1 text-xs text-red-500">{errors.website}</p>
        )}
      </div>

      <div className="pt-4 mt-auto">
        <button
          onClick={handleBusinessInfoNext}
          className={packPrimaryBtn}
        >
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </button>
      </div>
    </div>
  );

  const renderBusinessDetailsStep = () => (
    <div className={`space-y-4 ${packSurface ? 'flex flex-col min-h-[28rem]' : ''}`}>
      {packSurface ? renderPackStepHeader('Tell us more about your business structure') : (
      <div className="flex items-center mb-4">
        <button
          onClick={handleBack}
          className="mr-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
          disabled={isLoading}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Business Details</h2>
          <p className="text-sm text-gray-600">Tell us more about your business structure</p>
        </div>
      </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Business Type <span className="text-red-500">*</span>
        </label>
        <select
          name="businessType"
          value={formData.businessType}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
            errors.businessType ? 'border-red-500' : 'border-gray-300'
          }`}
          disabled={isLoading}
        >
          <option value="">Select business type</option>
          {BUSINESS_TYPES.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
        {errors.businessType && (
          <p className="mt-1 text-xs text-red-500">{errors.businessType}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Accounting Method <span className="text-red-500">*</span>
          <button
            type="button"
            className="ml-1 inline-flex relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            disabled={isLoading}
          >
            <Info className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            {showTooltip && (
              <div className="absolute bottom-full left-0 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-10">
                <p className="mb-1"><strong>Cash Basis:</strong> Record income when received, expenses when paid</p>
                <p><strong>Accrual Basis:</strong> Record income when earned, expenses when incurred</p>
              </div>
            )}
          </button>
        </label>
        <select
          name="accountingMethod"
          value={formData.accountingMethod}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg ${packFieldFocus} transition-colors ${
            errors.accountingMethod ? 'border-red-500' : 'border-gray-300'
          }`}
          disabled={isLoading}
        >
          <option value="cash">Cash Basis</option>
          <option value="accrual">Accrual Basis</option>
        </select>
        {formData.accountingMethod === 'cash' && (
          <p className="mt-1 text-sm text-gray-500">Most small businesses use cash basis</p>
        )}
        {errors.accountingMethod && (
          <p className="mt-1 text-xs text-red-500">{errors.accountingMethod}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Industry <span className="text-gray-400 text-xs">(Optional)</span>
        </label>
        <select
          name="industryType"
          value={formData.industryType}
          onChange={handleChange}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${packFieldFocus} transition-colors`}
          disabled={isLoading}
        >
          <option value="">Select your industry</option>
          {industryOptions.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {packSurface && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Site functions <span className="text-gray-400 text-xs">(Optional)</span>
          </label>
          <p className="text-xs text-gray-500 mb-2">
            What this location does. You can leave this unset.
          </p>
          <div className="space-y-2">
            {LOCATION_FUNCTIONS.map((fn) => {
              const checked = formData.locationFunctions.includes(fn.value);
              return (
                <label key={fn.value} className="flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className={`mr-2 rounded border-gray-300 ${
                      packSurface ? 'text-brand focus:ring-brand' : 'text-indigo-600 focus:ring-indigo-500'
                    }`}
                    checked={checked}
                    disabled={isLoading}
                    onChange={() => {
                      setFormData((prev) => {
                        const next = checked
                          ? prev.locationFunctions.filter((value) => value !== fn.value)
                          : [...prev.locationFunctions, fn.value];
                        return { ...prev, locationFunctions: next };
                      });
                    }}
                  />
                  {fn.label}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {errors.submit && (
        <div className="rounded-md bg-red-50 p-3 flex items-start">
          <p className="text-sm text-red-800">{errors.submit}</p>
        </div>
      )}

      <div className="pt-4 mt-auto">
        <button
          onClick={handleBusinessDetailsNext}
          disabled={isLoading}
          className={`${packPrimaryBtn} disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isLoading ? 'Creating Company...' : 'Create Company'}
        </button>
      </div>
    </div>
  );

  const renderPaymentSetupStep = () => (
    <div className="text-center">
      <div className="flex justify-center mb-4">
        <CheckCircle className="h-16 w-16 text-green-500" />
      </div>

      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        Company Created Successfully!
      </h3>
      <p className="text-gray-600 mb-6">
        Your business account is ready to use
      </p>

      {companyData && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg text-left">
          <p className="text-sm font-medium text-gray-700 mb-1">
            {companyData.businessName}
          </p>
          <div className="text-xs text-gray-500 space-y-1">
            {companyData.midStatus === 'test' && (
              <p className="text-green-600 font-medium">✓ Test mode enabled - You can start creating invoices</p>
            )}
            {companyData.gatewayAssigned && (
              <p>✓ Payment gateway assigned</p>
            )}
            {companyData.chartOfAccountsCreated && (
              <p>✓ Chart of accounts initialized</p>
            )}
          </div>
        </div>
      )}

      {companyData?.midStatus === 'test' ? (
        <div className="mb-6 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Test Mode Active:</strong> You can create invoices and test the platform.
            To process real payments, complete the payment setup.
          </p>
        </div>
      ) : (
        <div className="mb-6 p-3 bg-yellow-50 rounded-lg">
          <p className="text-sm text-yellow-800">
            <strong>Payment Setup Required:</strong> Complete the payment setup to start processing transactions.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <button
          onClick={handleSetupPayments}
          className="w-full py-3 px-6 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors flex items-center justify-center"
        >
          Setup Payments
          <ArrowRight className="ml-2 h-4 w-4" />
        </button>

        <button
          onClick={handleSetupLater}
          className="w-full text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
        >
          Setup later
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${
        packSurface ? 'bg-gray-900/40' : 'bg-black bg-opacity-50'
      }`}
      data-pack-modal={packSurface ? 'true' : undefined}
      onClick={(e) => {
        if (e.target === e.currentTarget && !canClose) {
          e.stopPropagation();
        } else if (e.target === e.currentTarget && canClose) {
          handleClose();
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="relative w-full max-w-md max-h-[90vh] bg-white rounded-lg shadow-xl flex flex-col">
        {/* Close button (only visible in payment setup step) */}
        {canClose && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="p-6 overflow-y-auto flex-1">
          {/* Progress indicator */}
          {currentStep > MODAL_STEPS.WELCOME && (
            <div className="mb-6">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-400">Step {currentStep} of {detailStepCount}</span>
                <span className="text-gray-400">{Math.round((currentStep / detailStepCount) * 100)}% Complete</span>
              </div>
              <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    packSurface ? 'bg-brand' : 'bg-indigo-600'
                  }`}
                  style={{ width: `${(currentStep / detailStepCount) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Content */}
          {currentStep === MODAL_STEPS.WELCOME && renderWelcomeStep()}
          {currentStep === MODAL_STEPS.BUSINESS_INFO && renderBusinessInfoStep()}
          {currentStep === MODAL_STEPS.BUSINESS_DETAILS && renderBusinessDetailsStep()}
          {currentStep === MODAL_STEPS.PAYMENT_SETUP && renderPaymentSetupStep()}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;